import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Tags } from '~/api/managed-entities/tags.autogen';
import { DEFAULT_POLL_INTERVAL_MS } from '~/eds/src/constants/duration';
import { managedEntities } from '~/redux/api/managed-entities';

import type {
  CreateEntityTypePayload,
  EntityType,
  JobStatus,
  UpdateEntityTypePayload,
} from '../types/EntityType';

import { toEntityType } from '../api/mapEntityType';

// The managed-entities service scopes every request to the authenticated
// client, so these hooks take no clientId — mirroring the sibling unified-fields
// hooks used by this feature.
const LIST_PAGE_LIMIT = 1000;

const TERMINAL_JOB_STATUSES: JobStatus[] = ['complete', 'failed'];

// `null` means no job has ever run for this entity type (e.g. manual-mode
// types like Groups never get one) — that's a resting state, not "in
// progress", so it must not be treated the same as pending/processing.
const isJobInProgress = (jobStatus: JobStatus | null) =>
  jobStatus != null && !TERMINAL_JOB_STATUSES.includes(jobStatus);

export const useEntityTypes = (options: { skip?: boolean } = {}) => {
  const { data, isLoading } = managedEntities.useListEntityTypesQuery(
    { page: { limit: LIST_PAGE_LIMIT } },
    { skip: options.skip },
  );

  const entityTypes = useMemo(
    () => (data?.data ?? []).map(toEntityType),
    [data],
  );

  return { entityTypes, isLoading };
};

/** One table page of entity types, polled while any row's job is running. */
export const useEntityTypesPage = ({
  pageIndex,
  pageSize,
}: {
  pageIndex: number;
  pageSize: number;
}) => {
  const [isPolling, setIsPolling] = useState(true);

  const { data, currentData, isFetching, error, refetch } =
    managedEntities.useListEntityTypesQuery(
      {
        page: { limit: pageSize, offset: (pageIndex - 1) * pageSize },
        rtkQueryOptions: { showErrorToast: false },
      },
      { pollingInterval: isPolling ? DEFAULT_POLL_INTERVAL_MS : undefined },
    );

  const entityTypes = useMemo(
    () => (currentData?.data ?? []).map(toEntityType),
    [currentData],
  );
  const isLoading = isFetching && !currentData;

  useEffect(() => {
    setIsPolling(
      isLoading ||
        entityTypes.some((entityType) =>
          isJobInProgress(entityType.job_status),
        ),
    );
  }, [isLoading, entityTypes]);

  return {
    entityTypes,
    totalCount: data?.meta.total ?? 0,
    isLoading,
    error: currentData ? undefined : error,
    refetch,
  };
};

// Defaults to the shared list because `GET /entity-types/{id}` is admin-only, so
// an `entities`-only role 403s on it (EKP-61224). `poll` opts into that route for
// the one caller needing a live job_status, and its own refetch cadence with it.
export const useEntityType = (
  entityTypeId: string,
  options: { poll?: boolean } = {},
) => {
  const poll = Boolean(options.poll);
  const dispatch = useDispatch();
  const [isPolling, setIsPolling] = useState(true);
  const previousRef = useRef<{
    entityTypeId: string;
    jobStatus: JobStatus | null;
  } | null>(null);

  // Args must match `useEntityTypes` or this becomes a second cache entry.
  const listQuery = managedEntities.useListEntityTypesQuery(
    { page: { limit: LIST_PAGE_LIMIT } },
    { skip: poll || !entityTypeId },
  );

  const byIdQuery = managedEntities.useGetEntityTypeQuery(
    { entityTypeId },
    {
      skip: !poll || !entityTypeId,
      pollingInterval: isPolling ? DEFAULT_POLL_INTERVAL_MS : undefined,
    },
  );

  const listEntityType = useMemo(() => {
    const resource = listQuery.data?.data.find(({ id }) => id === entityTypeId);
    return resource ? toEntityType(resource) : undefined;
  }, [listQuery.data, entityTypeId]);

  const byIdEntityType = useMemo(
    () => (byIdQuery.data ? toEntityType(byIdQuery.data.data) : undefined),
    [byIdQuery.data],
  );

  const entityType = poll ? byIdEntityType : listEntityType;
  const { isLoading, isError } = poll ? byIdQuery : listQuery;

  useEffect(() => {
    setIsPolling(
      !isError && (!entityType || isJobInProgress(entityType.job_status)),
    );
  }, [entityType, isError]);

  // A completed grouping/discover job writes new groups in the background —
  // the Alias Mapping table's own query has no way to learn that on its own.
  // Invalidate it the moment a job finishes, regardless of which tab happens
  // to be open. Keyed by entityTypeId, not just status: this hook's caller
  // can swap ids without unmounting (a route param changing), so a stale
  // "previous" from a different entity type must not be compared against.
  useEffect(() => {
    const previous = previousRef.current;
    const currentJobStatus = entityType?.job_status ?? null;
    previousRef.current = { entityTypeId, jobStatus: currentJobStatus };
    const previousJobStatus =
      previous?.entityTypeId === entityTypeId ? previous.jobStatus : null;
    if (currentJobStatus === 'complete' && isJobInProgress(previousJobStatus)) {
      dispatch(
        managedEntities.util.invalidateTags([{ type: Tags.Group, id: 'LIST' }]),
      );
    }
  }, [entityType?.job_status, entityTypeId, dispatch]);

  return { entityType, isLoading };
};

export const useCreateEntityType = () => {
  const [trigger, { isLoading }] =
    managedEntities.useCreateEntityTypeMutation();

  const createEntityType = useCallback(
    async (payload: CreateEntityTypePayload): Promise<EntityType> => {
      const response = await trigger({
        createEntityTypeDto: payload,
      }).unwrap();
      return toEntityType(response.data);
    },
    [trigger],
  );

  return { createEntityType, isSubmitting: isLoading };
};

export const useUpdateEntityType = () => {
  const [trigger, { isLoading }] =
    managedEntities.useUpdateEntityTypeMutation();

  const updateEntityType = useCallback(
    async (
      entityTypeId: string,
      payload: UpdateEntityTypePayload,
    ): Promise<EntityType> => {
      const response = await trigger({
        entityTypeId,
        updateEntityTypeDto: payload,
      }).unwrap();
      return toEntityType(response.data);
    },
    [trigger],
  );

  return { updateEntityType, isSubmitting: isLoading };
};

export const useDeleteEntityType = () => {
  const [trigger, { isLoading }] =
    managedEntities.useDeleteEntityTypeMutation();

  const deleteEntityType = useCallback(
    (entityTypeId: string) => trigger({ entityTypeId }).unwrap(),
    [trigger],
  );

  return { deleteEntityType, isSubmitting: isLoading };
};

// Kicks off a discover-mode re-cluster for the system Parties entity type.
// Its cache invalidation (LIST plus the entity-type-scoped tag, see
// src/redux/api/managed-entities/enhancements.ts) covers both the list row
// and an open detail page, so callers don't need to refetch anything themselves.
export const useTriggerDiscover = () => {
  const [trigger, { isLoading }] = managedEntities.useTriggerDiscoverMutation();

  const triggerDiscover = useCallback(
    (entityTypeId: string) =>
      // The caller shows its own toast, including a cooldown-specific message
      // the generic error toast can't produce (it doesn't know the retry time),
      // so opt this call out of the shared auto-toast rather than showing both.
      trigger({
        entityTypeId,
        rtkQueryOptions: { showErrorToast: false },
      }).unwrap(),
    [trigger],
  );

  return { triggerDiscover, isSubmitting: isLoading };
};
